import Decimal from 'decimal.js';

/**
 * #147 — extracted from `RequestForm.tsx` so `ReviewerQueue.tsx`'s read-only
 * review-detail screen can compute the identical figures instead of
 * reimplementing them with `(qty * price).toFixed(2)` / `sum + qty*price`.
 * CLAUDE.md's own hard rule: "Money fields: string/Decimal only, never JS
 * floats" — a JS float total can legitimately disagree with a `Decimal` one
 * (e.g. `(1 * 1.005).toFixed(2)` is `"1.00"`, `Decimal('1.005').times(1)` is
 * `"1.01"`), which is exactly how the requester's and reviewer's screens could
 * show two different grand totals for the same request.
 */

/** Matches the shared package's `quantity`/`netPrice` regex — up to 4 decimal places. */
export const QUANTITY_PATTERN = /^\d+(\.\d{1,4})?$/;
export const NET_PRICE_PATTERN = /^\d+(\.\d{1,4})?$/;

/**
 * Decimal-safe line total. Returns `null` when either value isn't yet a valid
 * positive decimal string — used both while a line is still being edited
 * (`RequestForm`) and when rendering an already-submitted line
 * (`ReviewerQueue`), so a blank or malformed value must not throw or silently
 * coerce to 0.
 */
export function computeLineTotal(
  quantity: string | null | undefined,
  netPrice: string | null | undefined,
): Decimal | null {
  if (
    !quantity ||
    !netPrice ||
    !QUANTITY_PATTERN.test(quantity) ||
    Number(quantity) <= 0 ||
    !NET_PRICE_PATTERN.test(netPrice)
  ) {
    return null;
  }
  return new Decimal(netPrice).times(quantity);
}

/**
 * Sums a set of line totals, treating an unavailable (`null`) line — one
 * still mid-edit, or missing a value — as contributing 0 rather than blocking
 * the whole total.
 */
export function sumLineTotals(totals: readonly (Decimal | null)[]): Decimal {
  return totals.reduce<Decimal>((sum, total) => (total ? sum.plus(total) : sum), new Decimal(0));
}
